import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import imputableService, {
  ImputableCRUD,
  normalizeTipoCuentaCodigo,
  normalizeVisibleEnOption,
  VISIBLE_EN_OPTIONS
} from '../../services/imputableService';
import noImputableService, {
  NoImputable,
  formatNoImputableLabel
} from '../../services/noImputableService';
import SearchableSelect from '../../components/SearchableSelect';

const defaultForm: ImputableCRUD = {
  codImputable: '',
  nombre: '',
  imputable: 'S',
  tipo: '1',
  activo: 'N',
  visibleEn: 'Ninguno',
  codImputablePadre: '',
  descripcion: '',
  nroRubro: undefined
};

/** Coincide con MaxLength(60) de NomCuenta en API; el cupo aplica al texto sin espacios extremos. */
const NOM_CUENTA_MAX = 60;

const tiposImputable = [
  { value: '1', label: '1 — Activo' },
  { value: '2', label: '2 — Pasivo' },
  { value: '3', label: '3 — Ingresos' },
  { value: '4', label: '4 — Egresos' },
  { value: '5', label: '5 — Patrimonio neto' },
  { value: '6', label: '6 — Unidad' },
  { value: '9', label: '9 — Cuenta y orden' }
];

function rubroDisplayText(ni: NoImputable | null, nroRubro: number | undefined): string {
  if (ni) return formatNoImputableLabel(ni);
  if (nroRubro != null && nroRubro > 0) return `Rubro ${nroRubro}`;
  return '';
}

const ImputableForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codImputable } = useParams();
  const [searchParams] = useSearchParams();
  const nroRubroFromQuery = searchParams.get('nroRubro');
  const tipoFromQuery = searchParams.get('tipo');
  const [formData, setFormData] = useState<ImputableCRUD>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [noImputableSeleccionado, setNoImputableSeleccionado] = useState<NoImputable | null>(null);

  const [modalRubroOpen, setModalRubroOpen] = useState(false);
  const [modalRubroSearchInput, setModalRubroSearchInput] = useState('');
  const [modalRubroSearchTerm, setModalRubroSearchTerm] = useState('');
  const [modalRubroPage, setModalRubroPage] = useState(0);
  const [modalRubroRowsPerPage, setModalRubroRowsPerPage] = useState(10);
  const [modalRubroItems, setModalRubroItems] = useState<NoImputable[]>([]);
  const [modalRubroTotal, setModalRubroTotal] = useState(0);
  const [modalRubroLoading, setModalRubroLoading] = useState(false);

  const loadImputable = useCallback(async () => {
    if (!codImputable || codImputable === 'nuevo') return;
    try {
      setLoading(true);
      const imputable = await imputableService.getImputable(codImputable);
      if (imputable) {
        const vis = normalizeVisibleEnOption(imputable.visibleEn);
        setFormData({
          ...defaultForm,
          ...imputable,
          tipo: normalizeTipoCuentaCodigo(imputable.tipo),
          visibleEn: vis,
          activo: vis === 'Ninguno' ? 'N' : 'S',
          nroRubro: imputable.nroRubro
        });
        const nr = imputable.nroRubro;
        if (nr != null && nr > 0) {
          try {
            const ni = await noImputableService.getNoImputable(nr);
            setNoImputableSeleccionado(ni);
          } catch {
            setNoImputableSeleccionado(null);
          }
        } else {
          setNoImputableSeleccionado(null);
        }
      }
    } catch (error) {
      enqueueSnackbar('Error al cargar el imputable', { variant: 'error' });
      navigate('/contabilidad/imputables');
    } finally {
      setLoading(false);
    }
  }, [codImputable, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codImputable && codImputable !== 'nuevo') {
      loadImputable();
      return;
    }

    const base: ImputableCRUD = { ...defaultForm };
    let presetNr: number | undefined;
    if (nroRubroFromQuery) {
      const n = parseInt(nroRubroFromQuery, 10);
      if (!Number.isNaN(n) && n > 0) presetNr = n;
    }
    const tq = tipoFromQuery?.trim();
    const tipoPreset =
      tq && ['1', '2', '3', '4', '5', '6', '9'].includes(tq) ? tq : undefined;

    setFormData({
      ...base,
      ...(presetNr != null ? { nroRubro: presetNr } : {}),
      ...(tipoPreset ? { tipo: tipoPreset } : {})
    });
    setNoImputableSeleccionado(null);

    if (presetNr != null) {
      noImputableService
        .getNoImputable(presetNr)
        .then(setNoImputableSeleccionado)
        .catch(() => setNoImputableSeleccionado(null));
    }
  }, [codImputable, loadImputable, nroRubroFromQuery, tipoFromQuery]);

  useEffect(() => {
    const t = setTimeout(() => setModalRubroSearchTerm(modalRubroSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [modalRubroSearchInput]);

  const fetchModalNoImputables = useCallback(async () => {
    if (!modalRubroOpen) return;
    try {
      setModalRubroLoading(true);
      const res = await noImputableService.getNoImputables({
        searchTerm: modalRubroSearchTerm,
        pageNumber: modalRubroPage + 1,
        pageSize: modalRubroRowsPerPage
      });
      setModalRubroItems(res.items);
      setModalRubroTotal(res.totalCount);
    } catch {
      setModalRubroItems([]);
      setModalRubroTotal(0);
      enqueueSnackbar('Error al cargar no imputables', { variant: 'error' });
    } finally {
      setModalRubroLoading(false);
    }
  }, [modalRubroOpen, modalRubroPage, modalRubroRowsPerPage, modalRubroSearchTerm, enqueueSnackbar]);

  useEffect(() => {
    if (!modalRubroOpen) return;
    fetchModalNoImputables();
  }, [modalRubroOpen, fetchModalNoImputables]);

  const openModalRubro = () => {
    setModalRubroSearchInput('');
    setModalRubroSearchTerm(''); // sin esperar al debounce, la primera carga usa listado completo
    setModalRubroPage(0);
    setModalRubroOpen(true);
  };

  const closeModalRubro = () => {
    setModalRubroOpen(false);
  };

  const handleSelectModalRubro = (row: NoImputable) => {
    setNoImputableSeleccionado(row);
    setFormData((prev) => ({ ...prev, nroRubro: row.nroRubro }));
    setModalRubroOpen(false);
  };

  const clearRubro = () => {
    setNoImputableSeleccionado(null);
    setFormData((prev) => ({ ...prev, nroRubro: undefined }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nombre') {
      const t = value.trim();
      if (t.length > NOM_CUENTA_MAX) {
        setFormData((prev) => ({ ...prev, nombre: t.slice(0, NOM_CUENTA_MAX) }));
      } else {
        setFormData((prev) => ({ ...prev, nombre: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  /** Al salir del campo se quitan espacios sobrantes al inicio/fin; no se tocan los del medio. */
  const handleNombreBlur = () => {
    setFormData((prev) => ({ ...prev, nombre: (prev.nombre || '').trim() }));
  };

  const handleSwitchChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.checked ? 'S' : 'N' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!formData.codImputable.trim()) {
        enqueueSnackbar('El código del imputable es requerido', { variant: 'warning' });
        return;
      }

      if (!formData.nombre.trim()) {
        enqueueSnackbar('El nombre del imputable es requerido', { variant: 'warning' });
        return;
      }

      const nr = formData.nroRubro;
      if (nr == null || nr <= 0) {
        enqueueSnackbar('Debe seleccionar un no imputable (Nro. rubro)', { variant: 'warning' });
        return;
      }

      if (codImputable && codImputable !== 'nuevo') {
        await imputableService.updateImputable(codImputable, formData);
      } else {
        await imputableService.createImputable(formData);
      }

      enqueueSnackbar('Imputable guardado exitosamente', { variant: 'success' });
      navigate('/contabilidad/imputables');
    } catch (error: any) {
      console.error('Error al guardar imputable:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el imputable', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {codImputable && codImputable !== 'nuevo' ? 'Editar Imputable' : 'Nuevo Imputable'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Configuración" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="codImputable"
                    label="Código"
                    value={formData.codImputable}
                    onChange={handleChange}
                    fullWidth
                    required
                    disabled={!!(codImputable && codImputable !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    name="nombre"
                    label="Nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    onBlur={handleNombreBlur}
                    fullWidth
                    required
                    helperText={`${(formData.nombre || '').trim().length}/${NOM_CUENTA_MAX}`}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="descripcion"
                    label="Descripción"
                    value={formData.descripcion || ''}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label="No imputable (Nro. rubro)"
                      value={rubroDisplayText(noImputableSeleccionado, formData.nroRubro)}
                      placeholder="Sin rubro asignado"
                      fullWidth
                      required
                      onClick={openModalRubro}
                      helperText="Pulse el campo o la lupa para elegir el no imputable; se guarda el Nro. rubro."
                      sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: 2, cursor: 'pointer' },
                        '& .MuiInputBase-input': { cursor: 'pointer' }
                      }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              type="button"
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                openModalRubro();
                              }}
                              aria-label="Buscar no imputable"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    {(noImputableSeleccionado || (formData.nroRubro != null && formData.nroRubro > 0)) && (
                      <Button
                        type="button"
                        variant="text"
                        color="secondary"
                        onClick={clearRubro}
                        sx={{ textTransform: 'none', mt: 1, flexShrink: 0 }}
                      >
                        Quitar
                      </Button>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <SearchableSelect
                    name="tipo"
                    label="Tipo de cuenta"
                    value={formData.tipo || null}
                    onChange={(v) => setFormData((prev) => ({ ...prev, tipo: v ?? '' }))}
                    options={tiposImputable}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SearchableSelect
                    name="visibleEn"
                    label="Visible en..."
                    value={normalizeVisibleEnOption(formData.visibleEn) || null}
                    onChange={(v) => {
                      const normalized = normalizeVisibleEnOption(v ?? '');
                      setFormData((prev) => ({
                        ...prev,
                        visibleEn: normalized,
                        activo: normalized === 'Ninguno' ? 'N' : 'S',
                      }));
                    }}
                    options={VISIBLE_EN_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="codImputablePadre"
                    label="Código Padre"
                    value={formData.codImputablePadre || ''}
                    onChange={handleChange}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.imputable === 'S'} onChange={handleSwitchChange('imputable')} />}
                    label="Es Imputable"
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/contabilidad/imputables')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <Dialog open={modalRubroOpen} onClose={closeModalRubro} fullWidth maxWidth="md" aria-labelledby="dialog-rubro-title">
        <DialogTitle id="dialog-rubro-title">Buscar no imputable (rubro)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por Nro. rubro, nombre o descripción…"
              value={modalRubroSearchInput}
              onChange={(e) => {
                setModalRubroSearchInput(e.target.value);
                setModalRubroPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TableContainer sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. rubro</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Seleccionar
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalRubroLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : modalRubroItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" color="text.secondary">
                        No hay resultados
                      </TableCell>
                    </TableRow>
                  ) : (
                    modalRubroItems.map((row) => (
                      <TableRow key={row.nroRubro} hover>
                        <TableCell>{row.nroRubro}</TableCell>
                        <TableCell>{(row.nombreCuenta || '').trim() || '—'}</TableCell>
                        <TableCell>{(row.descripcioncta || '').trim() || '—'}</TableCell>
                        <TableCell align="center">
                          <Button size="small" type="button" onClick={() => handleSelectModalRubro(row)}>
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 10, 20]}
              count={modalRubroTotal}
              rowsPerPage={modalRubroRowsPerPage}
              page={modalRubroPage}
              onPageChange={(_, p) => setModalRubroPage(p)}
              onRowsPerPageChange={(e) => {
                setModalRubroRowsPerPage(parseInt(e.target.value, 10));
                setModalRubroPage(0);
              }}
              labelRowsPerPage="Filas:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button type="button" onClick={closeModalRubro} sx={{ textTransform: 'none' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImputableForm;
